import { useCanRead, useCurrentUser, useHasFactoryAccess } from '@artic/api-client';
import { AppNav, type NavGroup } from '@artic/ui/app-nav';
import { Badge } from '@artic/ui/badge';
import { useFeatureFlag } from '@artic/ui/feature-flags';
import {
  AdminIcon,
  CatalogueIcon,
  DashboardsIcon,
  FactoryIcon,
  LegacyIcon,
  MaterialsIcon,
  OperatingCostsIcon,
  PlanningIcon,
  QualityIcon,
  SettingsIcon,
} from '@artic/ui/icons/nav';
import {
  getDashboardsUrl,
  getDrawingsUrl,
  getHubUrl,
  getStorybookUrl,
} from '@artic/ui/lib/environment';
import { Logo } from '@artic/ui/logo';
import { LogoMark } from '@artic/ui/logo-mark';
import { PartnerApiNavLink } from '@artic/ui/partner-api-nav-link';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@artic/ui/sidebar';
import { Link, useRouterState } from '@tanstack/react-router';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { usePartnerName } from '../features/inbox/use-partner-name';
import { NavUser } from './nav-user';
import { RouterNavLink } from './router-nav-link';

/**
 * Builds the translated navigation groups available to the current user.
 *
 * Navigation entries are configured according to feature flags, environment URLs,
 * administrator access, and legacy-grid availability.
 *
 * Dashboards (`dashboards-sidebar.tsx`) and the Hub (`AppRouter.tsx`, with Operating Costs,
 * Materials, Settings → Columns and Settings → Imports in `hooks/useOperatingCostsNavGroup.ts`,
 * `hooks/useMaterialsNavGroup.ts`, `hooks/useColumnsNavSubheader.ts` and
 * `hooks/useImportsNavSubheader.ts`) mirror these sections: add one to all three (ENG-1017).
 *
 * @returns The navigation groups for the current user
 */
function useNavItems(): NavGroup[] {
  const { t } = useTranslation('common');
  const { user } = useCurrentUser();
  const isAdmin = user.email.endsWith('@artic.works') || user.email.endsWith('@trunk.works');
  // Hidden rather than faded when off, matching how the Hub has always gated
  // its own `use_legacy_grid` entries: a partner off the legacy grid should not
  // be pointed back at the legacy pages at all.
  const useLegacyGrid = useFeatureFlag('use_legacy_grid');
  const neoWorkforce = useFeatureFlag('neo_workforce');
  // Cost to Produce is served by the operating_costs-guarded endpoints, so it needs both flags:
  // the item's own `cost_to_produce` flag, and `operating_costs` (disabled when that is off).
  const operatingCosts = useFeatureFlag('operating_costs');
  const drawingControlUrl = React.useMemo(() => getDrawingsUrl(), []);
  const dashboardsUrl = React.useMemo(() => getDashboardsUrl(), []);
  const storybookUrl = React.useMemo(() => getStorybookUrl(), []);
  // Permissions v1: a section the user cannot read drops out of the nav. Factory
  // is access, not a level: it shows to anyone with access to any factory.
  const canReadFactory = useHasFactoryAccess();
  const canReadQuality = useCanRead('quality');
  const canReadPlanner = useCanRead('planner');
  const canReadCatalog = useCanRead('catalog');
  const canReadMaterials = useCanRead('materials');
  const canReadCostToProduce = useCanRead('cost_to_produce');
  const canReadSettings = useCanRead('settings');

  return React.useMemo(() => {
    const groups: NavGroup[] = [
      {
        // ENG-866. Each dashboard is gated by its own flag; both are seeded
        // admin_only, so a partner user sees the entry disabled until it is
        // promoted for them. The Dashboards app is the flagged target itself,
        // so there is no fallback page to fade towards. Permissions v1:
        // Quality needs Quality read and Downtime needs Factory access — an
        // item the user can't see is hidden (the flag still greys), and the
        // group goes when neither is. Same rule in the Hub and Dashboards.
        title: t('terms.dashboards'),
        icon: DashboardsIcon,
        hide: !canReadQuality && !canReadFactory,
        defaultExpanded: true,
        items: [
          {
            title: t('terms.quality'),
            externalUrl: `${dashboardsUrl}/quality`,
            flag: 'quality_dashboard',
            hide: !canReadQuality,
          },
          {
            title: t('terms.downtime'),
            externalUrl: `${dashboardsUrl}/downtime`,
            flag: 'downtime_dashboard',
            hide: !canReadFactory,
          },
        ],
      },
      {
        title: t('terms.factory'),
        icon: FactoryIcon,
        hide: !canReadFactory,
        defaultExpanded: true,
        items: [
          { title: t('terms.statusboard'), hubPath: '/statusboard' },
          { title: t('terms.flow'), hubPath: '/schedules/progress' },
          // ENG-775: Job Cards now live in Neo. Ships unflagged — Hub Legacy's
          // own nav item and deep links forward here.
          { title: t('terms.jobCards'), to: '/job-cards' },
        ],
      },
      {
        title: t('terms.quality'),
        icon: QualityIcon,
        hide: !canReadQuality,
        defaultExpanded: true,
        items: [
          { title: t('terms.inbox'), to: '/inbox', flag: 'quality_inbox' },
          {
            title: t('terms.packs'),
            to: '/quality-packs',
          },
          { title: t('terms.visionQualityChecks'), hubPath: '/quality/vision-quality-checks' },
          {
            title: t('terms.visionTestPlans'),
            to: '/vision_test_plans',
          },
        ],
      },
      {
        title: t('terms.planner'),
        icon: PlanningIcon,
        hide: !canReadPlanner,
        defaultExpanded: true,
        items: [
          { title: t('terms.projects'), to: '/projects' },
          { title: t('terms.production'), to: '/production-planner' },
          {
            title: t('terms.changeControl'),
            to: '/change_control',
            flag: 'change_control',
          },
          neoWorkforce
            ? { title: t('terms.workforce'), to: '/workforce' }
            : { title: t('terms.workforce'), hubPath: '/workforce' },
          {
            title: t('terms.drawingControl'),
            externalUrl: drawingControlUrl,
            flag: 'drawing_control',
          },
        ],
      },
      {
        title: t('terms.catalogue'),
        icon: CatalogueIcon,
        hide: !canReadCatalog,
        defaultExpanded: true,
        items: [
          { title: t('terms.products'), to: '/products' },
          { title: t('terms.operations'), to: '/operations' },
          {
            title: t('terms.runSheets'),
            to: '/run_sheets',
            exact: true,
          },
        ],
      },
      // ENG-1022: Bill of Materials. Its imports sit under Settings → Imports.
      {
        title: t('terms.materials'),
        icon: MaterialsIcon,
        hide: !canReadMaterials,
        defaultExpanded: true,
        items: [{ title: t('terms.usage'), to: '/materials/usage', flag: 'bill_of_materials' }],
      },
      // ENG-871: deliberately its own section, not Settings — baselines change
      // as the plan changes.
      {
        title: t('terms.operatingCosts'),
        icon: OperatingCostsIcon,
        hide: !canReadCostToProduce,
        defaultExpanded: true,
        items: [
          {
            title: t('terms.overheads'),
            to: '/operating-costs/overheads',
            flag: 'operating_costs',
          },
          {
            title: t('terms.support'),
            to: '/operating-costs/support',
            flag: 'operating_costs',
          },
          {
            title: t('terms.valueAdding'),
            to: '/operating-costs/value-adding',
            flag: 'operating_costs',
          },
          { title: t('terms.labor'), to: '/operating-costs/labor', flag: 'operating_costs' },
          {
            title: t('terms.costToProduce'),
            to: '/operating-costs/cost-to-produce',
            flag: 'cost_to_produce',
            disabled: !operatingCosts,
          },
        ],
      },
      {
        title: t('terms.settings'),
        icon: SettingsIcon,
        hide: !canReadSettings,
        items: [
          {
            kind: 'subheader',
            label: t('terms.factory'),
            items: [
              {
                title: t('terms.factoryAdmin'),
                to: '/factories',
              },
              {
                title: t('terms.causeCategories'),
                to: '/cause_categories',
                flag: 'causes',
              },
              {
                title: t('terms.routes'),
                to: '/factory-routes',
                flag: 'routing',
              },
              {
                title: t('terms.stationsOperationsGrid'),
                to: '/operations-stations-grid',
              },
            ],
          },
          {
            kind: 'subheader',
            label: t('terms.columns'),
            items: [
              {
                title: t('terms.containers'),
                to: '/products/containers',
              },
              {
                title: t('terms.columns'),
                to: '/columns/structured-configuration',
              },
              {
                title: t('terms.customColumns'),
                to: '/columns/custom-configuration',
              },
              {
                // ENG-1035: the Bill of Materials fields and the partner's own.
                title: t('terms.materials'),
                to: '/columns/materials',
                flag: 'bill_of_materials',
              },
            ],
          },
          {
            kind: 'subheader',
            label: t('terms.imports'),
            items: [
              {
                title: t('terms.workOrderImports'),
                to: '/imports',
                flag: 'imports',
              },
              {
                title: t('terms.materialImports'),
                to: '/material-imports',
                flag: 'bill_of_materials',
                hide: !canReadMaterials,
              },
            ],
          },
          {
            kind: 'subheader',
            label: t('terms.workforce'),
            items: [
              {
                title: t('terms.shiftPatterns'),
                to: '/shift-patterns',
                flag: 'capacity_plan',
              },
            ],
          },
          {
            kind: 'subheader',
            label: t('terms.users'),
            items: [{ title: t('terms.permissions'), to: '/permissions', flag: 'permissions' }],
          },
        ],
      },
      {
        title: t('terms.articAdmin'),
        icon: AdminIcon,
        hide: !isAdmin,
        items: [
          // Always on for admins (ENG-628); the partner-facing counterpart
          // lives under Settings > Columns.
          {
            title: t('terms.containerLibrary'),
            to: '/admin/containers',
          },
          {
            title: t('terms.auditViewer'),
            to: '/audit',
            exact: true,
          },
          { title: t('terms.featureFlags'), to: '/admin/feature-flags' },
          { title: t('terms.partners'), to: '/admin/partners' },
          { title: t('terms.devices'), to: '/admin/devices' },
          {
            kind: 'subheader',
            label: t('terms.resources'),
            items: [
              storybookUrl
                ? { title: t('terms.storybook'), externalUrl: storybookUrl }
                : { title: t('terms.storybook'), disabled: true },
            ],
          },
        ],
      },
      {
        title: t('terms.legacy'),
        icon: LegacyIcon,
        hide: !isAdmin,
        items: [
          { title: t('terms.liveFeedQuality'), hubPath: '/feed' },
          { title: t('terms.gridSchedule'), hubPath: '/schedules/overview' },
          { title: t('terms.profiles'), hubPath: '/profiles' },
          {
            title: t('terms.drawingsLegacy'),
            hubPath: '/drawings',
            hide: !useLegacyGrid,
          },
        ],
      },
    ];

    return groups;
  }, [
    isAdmin,
    useLegacyGrid,
    neoWorkforce,
    operatingCosts,
    dashboardsUrl,
    drawingControlUrl,
    storybookUrl,
    canReadFactory,
    canReadQuality,
    canReadPlanner,
    canReadCatalog,
    canReadMaterials,
    canReadCostToProduce,
    canReadSettings,
    t,
  ]);
}

/**
 * Renders the Neo sidebar. The Partner API footer link requires both the
 * partner's feature flag and the user's read access to that section.
 */
export function QualitySidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation('common');
  const hubUrl = React.useMemo(() => getHubUrl(), []);
  const { state } = useSidebar();
  const partnerName = usePartnerName();
  const navItems = useNavItems();
  const canReadPartnerApi = useCanRead('partner_api');
  const currentPath = useRouterState({
    select: (state) => state.location.pathname,
  });

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              tooltip={t('terms.quality')}
              className="h-auto justify-center py-3"
            >
              <Link to="/">
                {state === 'collapsed' ? (
                  <LogoMark size="sm" />
                ) : (
                  <div className="flex w-full min-w-0 flex-col items-center gap-2">
                    <Logo className="h-8! w-auto!" />
                    <Badge
                      variant="secondary"
                      className="border-border text-muted-foreground max-w-full px-3"
                    >
                      <span className="truncate">{partnerName ?? 'Artic'}</span>
                    </Badge>
                  </div>
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <AppNav
          groups={navItems}
          LinkComponent={RouterNavLink}
          currentPath={currentPath}
          hubUrl={hubUrl}
          label={t('navigation.label')}
          unavailableLabel={t('navigation.notAvailableForYourAccount')}
        />
      </SidebarContent>
      <SidebarFooter>
        {/* Mirrored in the Dashboards and Hub sidebar footers. */}
        <PartnerApiNavLink label={t('terms.partnerApi')} canRead={canReadPartnerApi} />
        <NavUser />
        <p className="text-muted-foreground text-center text-[10px] select-all">{__COMMIT_SHA__}</p>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
